import dayjs from 'dayjs';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

import {
  ImageResizeResponse,
  ImageCompressionMetrics,
  ImageCompressionOptions,
} from './types';

export function compareStringAsc(a: string, b: string) {
  if (a.toLowerCase() < b.toLowerCase()) {
    return -1;
  }
  if (a.toLowerCase() > b.toLowerCase()) {
    return 1;
  }
  return 0;
}

export function compareStringDesc(a: string, b: string) {
  if (a.toLowerCase() < b.toLowerCase()) {
    return 1;
  }
  if (a.toLowerCase() > b.toLowerCase()) {
    return -1;
  }
  return 0;
}

export function compareDateAsc(a: any, b: any, property?: string) {
  const aMoment = property ? dayjs(a[property]) : dayjs(a);
  const bMoment = property ? dayjs(b[property]) : dayjs(b);

  return aMoment.diff(bMoment);
}

export function compareDateDesc(a: string, b: string) {
  const aMoment = dayjs(a);
  const bMoment = dayjs(b);
  return bMoment.diff(aMoment);
}

export function removeDuplicatesFromArray<T>(array: T[]) {
  return array.filter((thing, index) => {
    const _thing = JSON.stringify(thing);
    return index === array.findIndex((obj) => JSON.stringify(obj) === _thing);
  });
}

export function downloadFile(absoluteUrl: string, name: string): void {
  const link = document.createElement('a');
  link.href = absoluteUrl;
  link.download = name;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // delete link;
}

export function openUrl(absoluteUrl: string): void {
  const link = document.createElement('a');
  link.href = absoluteUrl;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export async function downloadFromUrl(
  url: string,
  filename: string,
): Promise<void> {
  try {
    // Fetch the file from the URL (works with CloudFront signed URLs)
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'omit', // Don't send cookies to avoid CORS issues
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Convert response to blob
    const blob = await response.blob();

    // Create object URL and download using existing downloadFile function
    const objectUrl = URL.createObjectURL(blob);
    downloadFile(objectUrl, filename);

    // Clean up the object URL to free memory
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  } catch (error) {
    console.warn('Direct download failed, trying fallback method:', error);

    // Fallback: try window.open as last resort
    // This might still be blocked by popup blocker, but it's better than nothing
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (fallbackError) {
      console.error('All download methods failed:', fallbackError);
      throw new Error(
        'Unable to download file. Please check the URL and try again.',
      );
    }
  }
}

export function arraysEqual<T>(_arr1: T[], _arr2: T[]): boolean {
  if (
    !Array.isArray(_arr1) ||
    !Array.isArray(_arr2) ||
    _arr1.length !== _arr2.length
  )
    return false;

  const arr1 = _arr1.concat().sort();
  const arr2 = _arr2.concat().sort();

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }

  return true;
}

export function isArrayOfType<T>(
  array: T[] | string[] | undefined,
): array is T[] {
  if (!Array.isArray(array)) {
    return false;
  }
  if (array.length === 0) {
    return true;
  }
  return !array.some((v: any) => typeof v === 'string');
}

export function isString<T>(value: T | string): value is string {
  return typeof value === 'string';
}

export function isStringArray<T>(array: T[] | string[]): array is string[] {
  if (!Array.isArray(array)) {
    return false;
  }
  if (array.length === 0) {
    return true;
  }
  let flag = true;
  array.forEach((v: any) => {
    if (!isString(v)) {
      flag = false;
    }
  });
  return flag;
}

export function assertExists<T>(value: T): asserts value is NonNullable<T> {
  if (value === null || value === undefined) {
    throw new Error('Expected `value` to not be null or undefined');
  }
}

/**
 * Reads a picked image into an in-memory File immediately.
 * Prevents Android gallery proxy files from becoming unreadable later.
 */
export async function materializeImageFile(
  file: File,
  fileName?: string,
): Promise<File> {
  const buffer = await file.arrayBuffer();
  const type = file.type || 'image/jpeg';
  const name = fileName || file.name || `image-${Date.now()}.jpg`;
  return new File([buffer], name, { type, lastModified: Date.now() });
}

export async function resizeImage(
  imageFile: File,
  maxSize = 1080,
  overrideType = false,
): Promise<ImageResizeResponse> {
  const startTime = performance.now();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (fileProgressEvent) {
      const dataUrl = fileProgressEvent.target?.result as string;
      if (!dataUrl) {
        reject(new Error('Failed to read image file'));
        return;
      }

      const imgToResize = new Image();

      imgToResize.onload = function () {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) {
          reject(new Error('Failed to get canvas context'));
        } else {
          const originalWidth = imgToResize.width;
          const originalHeight = imgToResize.height;

          let resizingFactor = 1;
          const widthResizeFactor = maxSize / originalWidth;
          const heightResizeFactor = maxSize / originalHeight;
          if (widthResizeFactor < 1 || heightResizeFactor < 1) {
            resizingFactor =
              widthResizeFactor < heightResizeFactor
                ? widthResizeFactor
                : heightResizeFactor;
          }

          const calculatedWidth = originalWidth * resizingFactor;
          const calculatedHeight = originalHeight * resizingFactor;

          canvas.width = calculatedWidth;
          canvas.height = calculatedHeight;

          // white background for transparent png's
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);

          context.drawImage(
            imgToResize,
            0,
            0,
            originalWidth * resizingFactor,
            originalHeight * resizingFactor,
          );

          const mimeType = overrideType ? imageFile.type : 'image/jpeg';
          const extension = overrideType
            ? `.${imageFile.name.split('.').pop() || ''}`
            : '.jpg';

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to create blob'));
              } else {
                const processingTime = performance.now() - startTime;
                const reductionPercent =
                  ((imageFile.size - blob.size) / imageFile.size) * 100;

                const metrics: ImageCompressionMetrics = {
                  originalSize: imageFile.size,
                  compressedSize: blob.size,
                  reductionPercent,
                  originalDimensions: {
                    width: originalWidth,
                    height: originalHeight,
                  },
                  resizedDimensions: {
                    width: Math.round(calculatedWidth),
                    height: Math.round(calculatedHeight),
                  },
                  processingTimeMs: processingTime,
                };

                console.log(`[resizeImage] ${imageFile.name}:`, {
                  original: `${(imageFile.size / 1024 / 1024).toFixed(
                    2,
                  )}MB (${originalWidth}x${originalHeight})`,
                  resized: `${(blob.size / 1024 / 1024).toFixed(
                    2,
                  )}MB (${Math.round(calculatedWidth)}x${Math.round(
                    calculatedHeight,
                  )})`,
                  reduction: `${reductionPercent.toFixed(1)}%`,
                  time: `${processingTime.toFixed(0)}ms`,
                });

                resolve({
                  blob,
                  extension,
                  mimeType,
                  metrics,
                });
              }
            },
            mimeType,
            // optional compression ration for file types that support lossy compression (e.g. jpg or webp)
            // 0.1,
          );
        }
      };

      imgToResize.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      // entry point for image.onload
      imgToResize.src = dataUrl;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    // entry point for reader.onload
    reader.readAsDataURL(imageFile);
  });
}

export async function compressImage(
  imageFile: File,
  options: ImageCompressionOptions = {},
): Promise<ImageResizeResponse> {
  const {
    maxDimension = 1080,
    quality = 0.85,
    targetMaxSizeMB = 2,
    format = 'jpeg',
    enableLogging = true,
  } = options;

  const startTime = performance.now();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function (fileProgressEvent) {
      const dataUrl = fileProgressEvent.target?.result as string;
      if (!dataUrl) {
        reject(new Error('Failed to read image file'));
        return;
      }

      const img = new Image();

      img.onload = function () {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          const originalWidth = img.width;
          const originalHeight = img.height;

          // Calculate resize factor
          let resizeFactor = 1;
          const widthFactor = maxDimension / originalWidth;
          const heightFactor = maxDimension / originalHeight;

          if (widthFactor < 1 || heightFactor < 1) {
            resizeFactor = Math.min(widthFactor, heightFactor);
          }

          const targetWidth = Math.round(originalWidth * resizeFactor);
          const targetHeight = Math.round(originalHeight * resizeFactor);

          canvas.width = targetWidth;
          canvas.height = targetHeight;

          // White background for transparent images
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Better image smoothing for quality
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Determine output format
          let outputFormat = 'image/jpeg';
          let outputExtension = '.jpg';

          if (format === 'webp') {
            outputFormat = 'image/webp';
            outputExtension = '.webp';
          } else if (
            format === 'original' &&
            imageFile.type.startsWith('image/')
          ) {
            outputFormat = imageFile.type;
            outputExtension = `.${imageFile.name.split('.').pop() || 'jpg'}`;
          }

          // Try compression with initial quality
          const tryCompress = (attemptQuality: number) => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to create blob'));
                  return;
                }

                const processingTime = performance.now() - startTime;
                const reductionPercent =
                  ((imageFile.size - blob.size) / imageFile.size) * 100;
                const compressedSizeMB = blob.size / 1024 / 1024;

                // Check if we need further compression
                if (
                  targetMaxSizeMB &&
                  compressedSizeMB > targetMaxSizeMB &&
                  attemptQuality > 0.5
                ) {
                  // Recursive compression with lower quality
                  const newQuality = attemptQuality - 0.1;
                  if (enableLogging) {
                    console.log(
                      `[compressImage] Target size not met (${compressedSizeMB.toFixed(
                        2,
                      )}MB > ${targetMaxSizeMB}MB), retrying with quality ${newQuality.toFixed(
                        2,
                      )}`,
                    );
                  }
                  tryCompress(newQuality);
                  return;
                }

                // Success - either met target or reached minimum quality
                const metrics: ImageCompressionMetrics = {
                  originalSize: imageFile.size,
                  compressedSize: blob.size,
                  reductionPercent,
                  originalDimensions: {
                    width: originalWidth,
                    height: originalHeight,
                  },
                  resizedDimensions: {
                    width: targetWidth,
                    height: targetHeight,
                  },
                  processingTimeMs: processingTime,
                };

                if (enableLogging) {
                  console.log(`[compressImage] ${imageFile.name}:`, {
                    original: `${(imageFile.size / 1024 / 1024).toFixed(
                      2,
                    )}MB (${originalWidth}x${originalHeight})`,
                    compressed: `${compressedSizeMB.toFixed(
                      2,
                    )}MB (${targetWidth}x${targetHeight})`,
                    reduction: `${reductionPercent.toFixed(1)}%`,
                    quality: attemptQuality.toFixed(2),
                    format: outputFormat,
                    time: `${processingTime.toFixed(0)}ms`,
                  });
                }

                // Fallback: if compressed is larger than original, use original
                if (blob.size > imageFile.size) {
                  if (enableLogging) {
                    console.warn(
                      `[compressImage] Compressed file larger than original, returning original`,
                    );
                  }
                  resolve({
                    blob: imageFile,
                    extension: `.${imageFile.name.split('.').pop() || 'jpg'}`,
                    mimeType: imageFile.type,
                    metrics: {
                      ...metrics,
                      compressedSize: imageFile.size,
                      reductionPercent: 0,
                    },
                  });
                } else {
                  resolve({
                    blob,
                    extension: outputExtension,
                    mimeType: outputFormat,
                    metrics,
                  });
                }
              },
              outputFormat,
              attemptQuality,
            );
          };

          // Start compression
          tryCompress(quality);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = dataUrl;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(imageFile);
  });
}

export function isClipboardEvent(event: Event): event is ClipboardEvent {
  return 'clipboardData' in event;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
